import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  Plus, 
  Car, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Send, 
  DollarSign,
  Clock,
  Loader2,
  Filter,
  Fuel
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DriveOffDebtPage = () => {
  const { user: authUser } = useAuth();
  const isStaff = authUser?.user_type === 'staff';
  
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [incidents, setIncidents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('open_all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialog states
  const [newIncidentOpen, setNewIncidentOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [writeOffDialogOpen, setWriteOffDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  
  // New incident form
  const [incidentForm, setIncidentForm] = useState({
    incident_type: 'drive_off',
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: '',
    pump_number: '',
    fuel_grade_id: '',
    fuel_grade_name: '',
    litres: '',
    amount: '',
    vehicle_registration: '',
    vehicle_description: '',
    police_reference: '',
    police_station: '',
    cctv_available: false,
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    agreement_signed: false,
    notes: ''
  });
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    payment_type: 'store',
    payment_method: 'cash',
    notes: ''
  });
  
  // Write off form
  const [writeOffNotes, setWriteOffNotes] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only fuel stations
      const fuelStations = response.data.filter(s => s.store_type === 'fuel_station');
      setStores(fuelStations);
      if (fuelStations.length > 0) {
        setSelectedStore(fuelStations[0].id);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    }
  };

  const fetchIncidents = useCallback(async () => {
    if (!selectedStore) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('store_id', selectedStore);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('incident_type', typeFilter);
      
      const [incidentsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/incidents?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/incidents/summary/${selectedStore}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setIncidents(incidentsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [selectedStore, statusFilter, typeFilter]);

  useEffect(() => {
    if (selectedStore) {
      fetchIncidents();
    }
  }, [selectedStore, fetchIncidents]);

  const getCurrentStore = () => {
    return stores.find(s => s.id === selectedStore);
  };

  const calculateAmount = (litres, gradeId) => {
    const store = getCurrentStore();
    if (!store || !litres || !gradeId) return 0;
    
    const grade = store.fuel_grades?.find(g => g.id === gradeId);
    if (!grade) return 0;
    
    // Price is in pennies, convert to pounds
    return (parseFloat(litres) * (grade.selling_price || 0)) / 100;
  };

  const handleLitresChange = (value) => {
    const litres = value;
    const amount = calculateAmount(litres, incidentForm.fuel_grade_id);
    setIncidentForm(prev => ({
      ...prev,
      litres: litres,
      amount: amount.toFixed(2)
    }));
  };

  const handleGradeChange = (gradeId) => {
    const store = getCurrentStore();
    const grade = store?.fuel_grades?.find(g => g.id === gradeId);
    const amount = calculateAmount(incidentForm.litres, gradeId);
    
    setIncidentForm(prev => ({
      ...prev,
      fuel_grade_id: gradeId,
      fuel_grade_name: grade?.name || '',
      amount: amount.toFixed(2)
    }));
  };

  const resetIncidentForm = () => {
    setIncidentForm({
      incident_type: 'drive_off',
      incident_date: new Date().toISOString().split('T')[0],
      incident_time: '',
      pump_number: '',
      fuel_grade_id: '',
      fuel_grade_name: '',
      litres: '',
      amount: '',
      vehicle_registration: '',
      vehicle_description: '',
      police_reference: '',
      police_station: '',
      cctv_available: false,
      customer_name: '',
      customer_phone: '',
      customer_address: '',
      agreement_signed: false,
      notes: ''
    });
  };

  const handleCreateIncident = async () => {
    if (!incidentForm.incident_time || !incidentForm.pump_number || !incidentForm.fuel_grade_id || !incidentForm.litres) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // For indebtedness, require credit agreement to be signed
    if (incidentForm.incident_type === 'indebtedness' && !incidentForm.agreement_signed) {
      toast.error('Credit agreement must be signed for indebtedness incidents');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/incidents`, {
        ...incidentForm,
        store_id: selectedStore,
        pump_number: parseInt(incidentForm.pump_number),
        litres: parseFloat(incidentForm.litres),
        amount: parseFloat(incidentForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Incident recorded successfully');
      setNewIncidentOpen(false);
      resetIncidentForm();
      fetchIncidents();
    } catch (error) {
      console.error('Error creating incident:', error);
      toast.error(error.response?.data?.detail || 'Failed to create incident');
    } finally {
      setSaving(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedIncident) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/incidents/${selectedIncident.id}/pay`, paymentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setSelectedIncident(null);
      setPaymentForm({ payment_type: 'store', payment_method: 'cash', notes: '' });
      fetchIncidents();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleSendToAgency = async (incident) => {
    if (!window.confirm('Are you sure you want to send this case to the collection agency?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/incidents/${incident.id}/send-to-agency`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Sent to collection agency');
      fetchIncidents();
    } catch (error) {
      console.error('Error sending to agency:', error);
      toast.error(error.response?.data?.detail || 'Failed to send to agency');
    }
  };

  const handleWriteOff = async () => {
    if (!selectedIncident) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/incidents/${selectedIncident.id}/write-off?notes=${encodeURIComponent(writeOffNotes)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Incident written off');
      setWriteOffDialogOpen(false);
      setSelectedIncident(null);
      setWriteOffNotes('');
      fetchIncidents();
    } catch (error) {
      console.error('Error writing off:', error);
      toast.error(error.response?.data?.detail || 'Failed to write off incident');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Open' },
      sent_to_agency: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Sent to Agency' },
      paid_store: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Paid (Store)' },
      paid_agency: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Paid (Agency)' },
      written_off: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Written Off' }
    };
    const badge = badges[status] || badges.open;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (type === 'drive_off') {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 flex items-center gap-1">
          <Car className="h-3 w-3" /> Drive Off
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 flex items-center gap-1">
        <FileText className="h-3 w-3" /> Indebtedness
      </span>
    );
  };

  const canWriteOff = user?.user_type === 'super_user' || user?.user_type === 'admin';
  const canSendToAgency = user?.user_type === 'super_user' || user?.user_type === 'admin' || user?.user_type === 'store_manager';

  if (stores.length === 0 && !loading) {
    return (
      <div className="p-6">
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Fuel Stations Found</h2>
            <p className="text-muted-foreground">
              Drive Off / Indebtedness management is only available for fuel station type stores.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-400" />
            Drive Off / Indebtedness
          </h1>
          <p className="text-muted-foreground">Manage fuel theft and debt incidents</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedStore} onValueChange={setSelectedStore}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select Store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>
                  {store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {!isStaff && (
            <Button onClick={() => setNewIncidentOpen(true)} data-testid="new-incident-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Incident
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Open Cases</p>
                  <p className="text-xl font-bold">{summary.open.count}</p>
                  <p className="text-sm font-mono text-amber-400">{formatCurrency(summary.open.amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Recovered</p>
                  <p className="text-xl font-bold">{summary.recovered.count}</p>
                  <p className="text-sm font-mono text-green-400">{formatCurrency(summary.recovered.amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Written Off</p>
                  <p className="text-xl font-bold">{summary.written_off.count}</p>
                  <p className="text-sm font-mono text-red-400">{formatCurrency(summary.written_off.amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Send className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">At Agency</p>
                  <p className="text-xl font-bold">{summary.by_status.sent_to_agency.count}</p>
                  <p className="text-sm font-mono text-blue-400">{formatCurrency(summary.by_status.sent_to_agency.amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filters:</span>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open_all">Open (All)</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="sent_to_agency">Sent to Agency</SelectItem>
                <SelectItem value="paid_store">Paid (Store)</SelectItem>
                <SelectItem value="paid_agency">Paid (Agency)</SelectItem>
                <SelectItem value="written_off">Written Off</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="drive_off">Drive Off</SelectItem>
                <SelectItem value="indebtedness">Indebtedness</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Incidents List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle>Incidents</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              No incidents found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3">ID</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Date/Time</th>
                    <th className="text-left p-3">Pump</th>
                    <th className="text-left p-3">Fuel</th>
                    <th className="text-right p-3">Litres</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-left p-3">Vehicle</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((incident) => (
                    <tr key={incident.id} className="border-b border-border hover:bg-muted/50">
                      <td className="p-3 font-mono text-sm">{incident.id}</td>
                      <td className="p-3">{getTypeBadge(incident.incident_type)}</td>
                      <td className="p-3 text-sm">
                        {incident.incident_date}<br />
                        <span className="text-muted-foreground">{incident.incident_time}</span>
                      </td>
                      <td className="p-3 text-center">{incident.pump_number}</td>
                      <td className="p-3">{incident.fuel_grade_name}</td>
                      <td className="p-3 text-right font-mono">{incident.litres?.toFixed(2)}</td>
                      <td className="p-3 text-right font-mono font-bold text-red-400">
                        {formatCurrency(incident.amount)}
                      </td>
                      <td className="p-3 text-sm">
                        {incident.vehicle_registration || '-'}<br />
                        <span className="text-muted-foreground text-xs">{incident.vehicle_description || ''}</span>
                      </td>
                      <td className="p-3">{getStatusBadge(incident.status)}</td>
                      <td className="p-3 text-right">
                        {(incident.status === 'open' || incident.status === 'sent_to_agency') && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedIncident(incident);
                                setPaymentDialogOpen(true);
                              }}
                              data-testid={`pay-btn-${incident.id}`}
                            >
                              <DollarSign className="h-3 w-3 mr-1" />
                              Pay
                            </Button>
                            
                            {incident.incident_type === 'indebtedness' && incident.status === 'open' && canSendToAgency && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendToAgency(incident)}
                                data-testid={`agency-btn-${incident.id}`}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Agency
                              </Button>
                            )}
                            
                            {canWriteOff && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => {
                                  setSelectedIncident(incident);
                                  setWriteOffDialogOpen(true);
                                }}
                                data-testid={`writeoff-btn-${incident.id}`}
                              >
                                <XCircle className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Incident Dialog */}
      <Dialog open={newIncidentOpen} onOpenChange={setNewIncidentOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Record New Incident
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Incident Type */}
            <div className="space-y-2">
              <Label>Incident Type</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="incident_type"
                    value="drive_off"
                    checked={incidentForm.incident_type === 'drive_off'}
                    onChange={(e) => setIncidentForm(prev => ({ ...prev, incident_type: e.target.value }))}
                    className="text-primary"
                  />
                  <Car className="h-4 w-4 text-red-400" />
                  <span>Drive Off</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="incident_type"
                    value="indebtedness"
                    checked={incidentForm.incident_type === 'indebtedness'}
                    onChange={(e) => setIncidentForm(prev => ({ ...prev, incident_type: e.target.value }))}
                    className="text-primary"
                  />
                  <FileText className="h-4 w-4 text-purple-400" />
                  <span>Indebtedness</span>
                </label>
              </div>
            </div>

            {/* Incident Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={incidentForm.incident_date}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">Must be today</p>
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={incidentForm.incident_time}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, incident_time: e.target.value }))}
                  data-testid="incident-time"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Pump Number *</Label>
                <Input
                  type="number"
                  value={incidentForm.pump_number}
                  onChange={(e) => setIncidentForm(prev => ({ ...prev, pump_number: e.target.value }))}
                  placeholder="1"
                  data-testid="pump-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Fuel Grade *</Label>
                <Select value={incidentForm.fuel_grade_id} onValueChange={handleGradeChange}>
                  <SelectTrigger data-testid="fuel-grade-select">
                    <SelectValue placeholder="Select Grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {getCurrentStore()?.fuel_grades?.map(grade => (
                      <SelectItem key={grade.id} value={grade.id}>
                        {grade.name} ({grade.selling_price}p/L)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Litres *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={incidentForm.litres}
                  onChange={(e) => handleLitresChange(e.target.value)}
                  placeholder="0.00"
                  data-testid="litres-input"
                />
              </div>
            </div>

            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Calculated Amount:</span>
                <span className="text-2xl font-bold font-mono text-red-400">
                  {formatCurrency(parseFloat(incidentForm.amount) || 0)}
                </span>
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Car className="h-4 w-4" /> Vehicle Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Registration</Label>
                  <Input
                    value={incidentForm.vehicle_registration}
                    onChange={(e) => setIncidentForm(prev => ({ ...prev, vehicle_registration: e.target.value.toUpperCase() }))}
                    placeholder="AB12 CDE"
                    data-testid="vehicle-reg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={incidentForm.vehicle_description}
                    onChange={(e) => setIncidentForm(prev => ({ ...prev, vehicle_description: e.target.value }))}
                    placeholder="Silver Ford Focus"
                    data-testid="vehicle-desc"
                  />
                </div>
              </div>
            </div>

            {/* Drive Off Specific */}
            {incidentForm.incident_type === 'drive_off' && (
              <div className="space-y-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <h4 className="font-medium text-amber-400">Police Report Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Police Reference</Label>
                    <Input
                      value={incidentForm.police_reference}
                      onChange={(e) => setIncidentForm(prev => ({ ...prev, police_reference: e.target.value }))}
                      placeholder="MET-2026-12345"
                      data-testid="police-ref"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Police Station</Label>
                    <Input
                      value={incidentForm.police_station}
                      onChange={(e) => setIncidentForm(prev => ({ ...prev, police_station: e.target.value }))}
                      placeholder="Wembley"
                      data-testid="police-station"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={incidentForm.cctv_available}
                    onChange={(e) => setIncidentForm(prev => ({ ...prev, cctv_available: e.target.checked }))}
                  />
                  <span>CCTV footage available</span>
                </label>
              </div>
            )}

            {/* Indebtedness Specific */}
            {incidentForm.incident_type === 'indebtedness' && (
              <div className="space-y-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <h4 className="font-medium text-purple-400">Customer Details (Credit Agreement)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Name</Label>
                    <Input
                      value={incidentForm.customer_name}
                      onChange={(e) => setIncidentForm(prev => ({ ...prev, customer_name: e.target.value }))}
                      placeholder="John Smith"
                      data-testid="customer-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={incidentForm.customer_phone}
                      onChange={(e) => setIncidentForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                      placeholder="07123 456789"
                      data-testid="customer-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea
                    value={incidentForm.customer_address}
                    onChange={(e) => setIncidentForm(prev => ({ ...prev, customer_address: e.target.value }))}
                    placeholder="123 High Street, London"
                    data-testid="customer-address"
                  />
                </div>
                <div className={`p-3 rounded-lg ${!incidentForm.agreement_signed ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={incidentForm.agreement_signed}
                      onChange={(e) => setIncidentForm(prev => ({ ...prev, agreement_signed: e.target.checked }))}
                      data-testid="agreement-signed-checkbox"
                    />
                    <span className="font-medium">Credit agreement signed *</span>
                    <span className="text-xs text-muted-foreground">(Required)</span>
                  </label>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={incidentForm.notes}
                onChange={(e) => setIncidentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                data-testid="incident-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewIncidentOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateIncident} disabled={saving} data-testid="save-incident-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-400" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Incident: {selectedIncident.id}</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(selectedIncident.amount)}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Payment Source</Label>
                <Select value={paymentForm.payment_type} onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="store">Paid at Store</SelectItem>
                    <SelectItem value="agency">Received from Agency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {paymentForm.payment_type === 'store' && (
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePayment} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write Off Dialog */}
      <Dialog open={writeOffDialogOpen} onOpenChange={setWriteOffDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="h-5 w-5" />
              Write Off Incident
            </DialogTitle>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-4">
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Incident: {selectedIncident.id}</p>
                <p className="text-2xl font-bold font-mono text-red-400">{formatCurrency(selectedIncident.amount)}</p>
                <p className="text-sm text-red-400 mt-2">This amount will be written off as unrecoverable.</p>
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Write Off</Label>
                <Textarea
                  value={writeOffNotes}
                  onChange={(e) => setWriteOffNotes(e.target.value)}
                  placeholder="Enter reason for writing off this incident..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriteOffDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleWriteOff} disabled={saving} variant="destructive">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Write Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriveOffDebtPage;
